import { makeAutoObservable } from 'mobx';
import { ApportionmentApi, ApportionmentApiResponse } from '../../../api/ApportionmentApi';
import { ApportionmentAssignmentAction, ApportionmentType, ApportionmentUnit, ApportionmentVisibility } from '../../../config/Constants';
import { Split } from './Split';
import { Assignment } from './Assignment';
import { NegativeIdGenerator } from '../../../utils/NegativeIdGenerator';
import { AssignmentSetting } from './AssignmentSetting';

export type OverrideRequirements = {
  nominalCode: boolean,
  trackingCategories: Map<string, boolean>
};

export class Apportionment {
  id: number = 0;
  groupId: number = 0;
  name: string = '';
  type: ApportionmentType = ApportionmentType.CROSS_ENTITY;
  visibility: ApportionmentVisibility = ApportionmentVisibility.GROUP;
  localTotal: number = 0;
  units: ApportionmentUnit = ApportionmentUnit.UNIT;
  splits: Split[] = [];
  isDeletable: boolean = false;

  totalError: boolean = false;
  nameError: boolean = false;

  // This temporarily holds the total in units for when the user switches between percentage and units
  unitsTotal: number = -1;

  nominalCodeAction: ApportionmentAssignmentAction = ApportionmentAssignmentAction.RETAIN;
  trackingCategoryActions: Map<string, ApportionmentAssignmentAction> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: ApportionmentApiResponse, groupId: number): Apportionment {
    const appt = new Apportionment();
    appt.parseApi(apiData, groupId);
    return appt;
  }

  parseApi(apiData: ApportionmentApiResponse, groupId: number) {
    if (apiData) {
      this.id = apiData.id;
      this.groupId = groupId;
      this.name = apiData.name;
      this.type = apiData.type as ApportionmentType;
      this.visibility = apiData.visibility as ApportionmentVisibility;
      this.localTotal = apiData.total;
      this.units = apiData.splitsUnit as ApportionmentUnit;
      this.isDeletable = apiData.deletable;
      this.nominalCodeAction = apiData.accountCodeAction as ApportionmentAssignmentAction || ApportionmentAssignmentAction.SET;
      this.trackingCategoryActions = new Map();
      
      Object.keys(apiData.trackingCategoryAction || {}).forEach(key =>
        this.trackingCategoryActions.set(key, apiData.trackingCategoryAction[key] as ApportionmentAssignmentAction)
      );
  
      this.splits = apiData.splits.map(
        split => Split.fromApi(split, groupId)
      ); 
    }
  }

  static intraEntityApportionment(groupId: number, entityId: number, assignment: Assignment = null, assignmentSettings: AssignmentSetting = null): Apportionment {
    const apportionment = new Apportionment();
    apportionment.id = NegativeIdGenerator.next();
    apportionment.groupId = groupId;
    apportionment.type = ApportionmentType.INTRA_ENTITY;
    apportionment.visibility = ApportionmentVisibility.RULE;
    apportionment.nominalCodeAction = ApportionmentAssignmentAction.SET;

    const newSplit = new Split();
    newSplit.split = 1;

    // Need to create new assignment instead of re-using otherwise subsequent updates may not be captured
    newSplit.subApportionment = assignment ? Assignment.fromAssignment(assignment) : Assignment.fromAssignmentSettings(entityId, assignmentSettings);

    apportionment.splits = [newSplit];
    apportionment.units = ApportionmentUnit.UNIT;
    apportionment.total = 1;

    return apportionment;
  }

  toApi(): ApportionmentApiResponse {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      total: this.total,
      visibility: this.visibility,
      splitsUnit: this.units,
      splits: this.splits.map(entry => entry.toApi()),
      accountCodeAction: this.nominalCodeAction,
      trackingCategoryAction: Object.fromEntries(this.trackingCategoryActions),
    };
  }

  get isTotalEditable(): boolean {
    return this.units !== ApportionmentUnit.PERCENTAGE;
  }

  get total(): number {
    return this.units === ApportionmentUnit.PERCENTAGE
      ? 100
      : Math.max(
        this.localTotal,
        this.apportionmentSum
      );
  }

  set total(total: number) {
    this.localTotal = total;
  }

  get apportionmentSum(): number {
    return +(this.splits.map(e => e.split).reduce((a, b) => a + b, 0)).toFixed(12);
  }

  get hasBeenPersisted(): boolean {
    return this.id > 0;
  }

  get isValid(): boolean {
    return this.validate() == null;
  }

  validate(creatingEntity: number = null, assignmentSettings: Map<number, AssignmentSetting> = new Map(), ignorePercentageCheck: boolean = false): String {
    this.clearExistingValidationErrors();
    let validationError;
    validationError = this.validateNameAndSplitSizes(ignorePercentageCheck);
    if (validationError) return validationError;
    validationError = this.validateAssignmentSplits(creatingEntity, assignmentSettings);
    if (validationError) return validationError;
    if (this.type === ApportionmentType.INTRA_ENTITY) {
      validationError = this.validateIntraEntitySingleRetainSplit();
      if (validationError) return validationError;
      validationError = this.validateIntraEntityDuplicateApportionments(creatingEntity, assignmentSettings);
      if (validationError) return validationError;
    }
    if (this.type === ApportionmentType.CROSS_ENTITY) {
      // Run validation on nested apportionments
      validationError = this.splits
        .map(split => {
          if (split.subApportionment.type === ApportionmentType.INTRA_ENTITY && !assignmentSettings.get(split.siblingId)?.ignore) {
            const apportionment = split.subApportionment as Apportionment;
            return apportionment.validate(creatingEntity, assignmentSettings);
          }
        })
        .find(validationResult => validationResult);
    }
    return validationError || null;
  }

  addAssignmentSplit(entityId: number, value: number): Split {
    const split = new Split();
    split.split = value;

    const assignment = new Assignment();
    assignment.id = NegativeIdGenerator.next();
    assignment.entityId = entityId;
    split.subApportionment = assignment;

    this.splits.push(split);

    return split;
  }

  splitForEntity(entityId: number): Split {
    return this.splits.find(s => (s.subApportionment.type === ApportionmentType.ASSIGNMENT && s.subApportionment.entityId === entityId));
  }

  entities(assignmentSettings: Map<number, AssignmentSetting>): number[] {
    const ids: Set<number> = new Set();

    this.splits
      .filter(split => split.split > 0)
      .forEach(split => {
        if (assignmentSettings.has(split.siblingId) && assignmentSettings.get(split.siblingId).ignore) return;

        if (split.subApportionment.type === ApportionmentType.ASSIGNMENT) {
          ids.add(split.subApportionment.entityId);
        } else {
          (split.subApportionment as Apportionment).entities(assignmentSettings)
            .forEach(id => ids.add(id));
        }
      });

    return [...ids.values()];
  }

  assignmentIds(): number[] {
    const ids: Set<number> = new Set();

    this.splits
      .filter(split => split.split > 0)
      .forEach(split => {
        if (split.subApportionment.type === ApportionmentType.ASSIGNMENT) {
          ids.add(split.siblingId);
        } else {
          ids.add(split.siblingId); // Add the child ID in case we've overridden it
          (split.subApportionment as Apportionment).assignmentIds()
            .forEach(id => ids.add(id));
        }
      });

    return [...ids.values()];
  }

  get entityId(): number {
    if (this.type === ApportionmentType.CROSS_ENTITY) {
      return null;
    }
    return this.entities(new Map())[0];
  }

  save = (): Promise<void> => {
    const promise = this.id > 0
      ? ApportionmentApi.updateApportionment(this.groupId, this.id, this.toApi())
      : ApportionmentApi.createApportionment(this.groupId, this.toApi());

    return promise
      .then(response => {
        this.parseApi(response, this.groupId);
      });
  };

  changeUnits() {
    if (this.units === ApportionmentUnit.PERCENTAGE) {
      if (this.unitsTotal > 0) {
        this.splits.forEach(split => {
          split.split = (split.split * this.unitsTotal) / 100;
        });
        this.total = this.unitsTotal;
      } else {
        this.total = this.apportionmentSum;
      }

      this.units = ApportionmentUnit.UNIT;
    } else {
      this.unitsTotal = this.total;

      this.splits.forEach(split => {
        split.split = 100 * (split.split / this.unitsTotal);
      });

      this.total = 100;
      this.units = ApportionmentUnit.PERCENTAGE;
    }
  }

  private clearExistingValidationErrors() {
    this.nameError = false;
    this.totalError = false;
    this.splits
      .flatMap(split => {
        if (split.subApportionment.type === ApportionmentType.INTRA_ENTITY) {
          return (split.subApportionment as Apportionment)?.splits?.map(nestedSplit => nestedSplit.subApportionment as Assignment);
        } else {
          return [split.subApportionment as Assignment];
        }
      })
      .forEach(assignment => {
        if (assignment.codeError) assignment.codeError = false;
        if (assignment.categoryError) assignment.categoryError = false;
      });
  }

  private validateNameAndSplitSizes(ignorePercentageCheck: boolean = false): string {
    this.nameError = this.visibility === ApportionmentVisibility.GROUP && !this.name;
    if (this.nameError) return 'apportionment-NameError'; // Apportionment must have a distinct name

    this.totalError = this.apportionmentSum > this.total || this.apportionmentSum === 0;
    if (this.totalError) { // Sum of all splits cannot exceed splits total
      this.splits.forEach(split => split.valueError = true); // Ensures to highlight percentage boxes when total box isn't shown
      return 'apportionment-SplitsTotalError';
    }

    if (!ignorePercentageCheck && this.units === ApportionmentUnit.PERCENTAGE) {
      const sumError = this.apportionmentSum !== 100;
      this.splits.forEach(split => split.valueError = sumError);
      if (sumError) return 'apportionment-SplitsSumPercentageError'; // Apportionment splits must add up to 100%
    }

    if (this.splits.length === 0) return 'apportionment-NoSplits'; // Apportionment doesn't have any splits

    return null;
  }

  private validateAssignmentSplits(creatingEntity: number, assignmentSettings: Map<number, AssignmentSetting>): string {
    let validationError: string = null;
    this.splits.forEach(split => {
      if (split.subApportionment.type == ApportionmentType.ASSIGNMENT) {
        const assignment = split.subApportionment as Assignment;
        validationError = validationError || this.validateAssignment(assignmentSettings, split, assignment, creatingEntity);
      }
    });
    return validationError;
  }

  private validateAssignment(assignmentSettings: Map<number, AssignmentSetting>, split: Split, assignment: Assignment, creatingEntity: number) {
    const assignmentSettingNcExternalId = assignmentSettings?.get(split.siblingId)?.ncExternalId;
    const assignmentSettingIgnore = assignmentSettings?.get(split.siblingId)?.ignore;
    if (assignmentSettingIgnore) {
      return null;
    } else if (creatingEntity === null) {
      // Intra entity apportionment is being created
      if (this.nominalCodeAction === ApportionmentAssignmentAction.SET && !assignment.nominalCode?.externalId) {
        assignment.codeError = true;
        return 'apportionment-NominalCodeMustBeSet'; // Nominal code must be set for all apportionment splits
      }
    } else {
      // Rule is being created using existing intra-entity apportionment
      const creator = (split.entityId === creatingEntity);
      if (this.nominalCodeAction === ApportionmentAssignmentAction.SET && !assignment.nominalCode?.externalId && !assignmentSettingNcExternalId) {
        assignment.codeError = true;
        return 'apportionment-NominalCodeMustBeSet'; // Nominal code must be set for all apportionment splits
      } else if (this.nominalCodeAction === ApportionmentAssignmentAction.RETAIN && !creator && !assignmentSettingNcExternalId) {
        assignment.codeError = true;
        return 'apportionment-NominalCodeMustBeSet'; // Nominal code must be set for all apportionment splits
      }
    }
    return null;
  }

  private validateIntraEntityDuplicateApportionments(creatingEntity: number, assignmentSettings: Map<number, AssignmentSetting>): string {
    // Duplicate splits aren't allowed
    let validationError: string = null;
    const actions = [this.nominalCodeAction, ...this.trackingCategoryActions.values()];
    const foundSplits = new Map();
    const categoryKeys = Array.from(this.trackingCategoryActions.keys());
    this.splits.filter(split => !assignmentSettings?.get(split.siblingId)?.ignore).forEach(split => {
      const assignment = split.subApportionment as Assignment;
      const assignmentSetting = assignmentSettings?.get(split.siblingId);
      let serializedSplit = '';
      if (actions[0] === ApportionmentAssignmentAction.SET) serializedSplit += assignment.nominalCode?.externalId ?? '';
      if (actions[0] === ApportionmentAssignmentAction.RETAIN) serializedSplit += assignmentSetting?.ncExternalId ?? '';
      if (actions[1] === ApportionmentAssignmentAction.SET) serializedSplit += (assignment.trackingCategories.get(categoryKeys[0])?.nameId ?? '');
      if (actions[1] === ApportionmentAssignmentAction.RETAIN) serializedSplit += assignmentSetting?.trackingCategories?.get(categoryKeys[0]) ?? '';
      if (actions[2] === ApportionmentAssignmentAction.SET) serializedSplit += (assignment.trackingCategories.get(categoryKeys[1])?.nameId ?? '');
      if (actions[2] === ApportionmentAssignmentAction.RETAIN) serializedSplit += assignmentSetting?.trackingCategories?.get(categoryKeys[1]) ?? '';
      if (foundSplits.has(serializedSplit)) {
        validationError = 'apportionment-DuplicateSplits'; // Two apportionment splits are identical and can be merged
        assignment.codeError = true;
        assignment.categoryError = true;
        foundSplits.get(serializedSplit).codeError = true;
        foundSplits.get(serializedSplit).categoryError = true;
      } else {
        foundSplits.set(serializedSplit, assignment);
      }
    });
    return validationError;
  }

  private validateIntraEntitySingleRetainSplit(): string {
    // Duplicate splits aren't allowed
    if (this.splits.length === 1) {
      // A single split with all retain is pointless
      const distinctActions = new Set([this.nominalCodeAction, ...this.trackingCategoryActions.values()]);
      if (distinctActions.size === 1 && [...distinctActions.values()][0] === ApportionmentAssignmentAction.RETAIN) {
        (this.splits[0].subApportionment as Assignment).codeError = true;
        (this.splits[0].subApportionment as Assignment).categoryError = true;
        return 'apportionment-AllRetainSingleSplits'; // At least one of the apportionment properties must be set
      }
    }
    return null;
  }
}
